import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@/lib/AuthContext'
import type { FormAnswerInput } from '@/types/forms'
import { useCommunity } from '@/lib/CommunityContext'
import { sdk } from '@/lib/sdk'
import { usePendingPurchasePoll } from '@/lib/hooks/usePendingPurchasePoll'
import { useCheckoutConfig } from '@/lib/hooks/useCheckoutConfig'
import { usePagarme } from '@/lib/hooks/usePagarme'
import { usePriceQuote } from '@/lib/hooks/usePriceQuote'
import type { AppliedCoupon, PaymentMethod, PublicProduct } from '@/types/checkout'
import { defaultPaymentMethod, parseAllowedPaymentMethods } from '@/lib/checkout/paymentMethods'
import {
  CustomerData,
  CustomerField,
  emptyCustomerData,
  accountDocument,
  nationalBrazilianPhone,
  onlyDigits,
  toCustomerPayload,
  validateCustomerData
} from '@/lib/checkout/customer'
import { CardData, CardField, emptyCardData, toTokenizeInput, validateCardData } from '@/lib/checkout/card'
import { apiErrorMessage, CheckoutOutcome, CheckoutResponse, interpretCheckoutResponse } from '@/lib/checkout/outcome'
import { isYearly, yearlyInstallments } from '@/lib/checkout/annual'
import {
  emptyShipping,
  shippingFieldOf,
  toShippingPayload,
  validateShipping,
  type ShippingField,
  type ShippingForm
} from '@/lib/checkout/shipping'
import { gaItem, rememberOrder, trackAddPaymentInfo, trackBeginCheckout, trackPurchase, type GaCart } from '@/lib/analytics/ecommerce'
import { getGaIds } from '@/lib/hooks/useGoogleAnalytics'

export type PurchaseFieldErrors = Partial<Record<CustomerField | CardField, string>>

/**
 * A submit error. `key` is an i18n key in the `checkout` namespace; `message`
 * is the API's own text, already translated by the server.
 */
export interface PurchaseSubmitError {
  key?: string
  message?: string
  /** The buyer lacks the plan this add-on requires: the panel offers a way to the plans. */
  needsPlan?: boolean
  /** The buyer already holds this plan (409): the panel links to their subscription. */
  alreadySubscribed?: boolean
  /** F-74: the last place went to someone else; the panel offers the waitlist. */
  soldOut?: boolean
}

/** FIN-7: a coupon the server would not apply. `key` is an i18n key, `message` the API's text. */
export interface CouponError {
  key?: string
  message?: string
}

export interface PurchaseFlow {
  product: PublicProduct | null
  allowedMethods: PaymentMethod[]
  method: PaymentMethod | null
  setMethod: (method: PaymentMethod) => void
  installments: number
  setInstallments: (installments: number) => void
  /** Installments offered: only by card, for one-off products and annual options (F-36). */
  maxInstallments: number
  /**
   * An annual option (F-36): each installment option shows its amount. The
   * total is the same at 1× and 12× today; whether installments carry
   * interest becomes an option of its own (F-43), and the screen will say so then.
   */
  showInstallmentAmounts: boolean
  /** A subscription paid by card keeps the card as the default for renewals (FIN-51). */
  savesCard: boolean

  customer: CustomerData
  setCustomerField: (field: CustomerField, value: string) => void
  card: CardData
  setCardField: (field: CardField, value: string) => void
  fieldErrors: PurchaseFieldErrors
  /** The account has a CPF/CNPJ and the one typed is different: offer to keep the new one. */
  canSaveDocument: boolean
  saveDocument: boolean
  setSaveDocument: (save: boolean) => void

  /** F-74: price 0. No method, no buyer data, no card: the order is a claim. */
  isFree: boolean
  /**
   * F-204: "I am 18 or older, or authorized by my legal guardian", unchecked, on an order that costs
   * money; the order is not sent without it. Absent on a flow that does not go through `POST /checkout`.
   */
  ageDeclaration?: { checked: boolean; onChange: (checked: boolean) => void; error: boolean }
  /** A coupon took a one-off total to zero: still a claim, but the coupon stays visible so it can be removed. */
  couponZeroed: boolean
  /** F-74: a physical product asks where to send it. */
  needsShipping: boolean
  shipping: ShippingForm
  setShippingField: (field: ShippingField, value: string) => void
  shippingErrors: Partial<Record<ShippingField, string>>

  quote: ReturnType<typeof usePriceQuote>

  /** FIN-7: the coupon the current quote applied; null while none is, or while one is being checked. */
  appliedCoupon: AppliedCoupon | null
  /** The discount the applied coupon gives on this order. */
  discountAmount: number
  /** A code was sent and the server has not answered yet. */
  couponPending: boolean
  couponError: CouponError | null
  applyCoupon: (code: string) => void
  removeCoupon: () => void
  /** The amount to show as total, or null when it cannot be known yet. */
  totalAmount: number | null
  currency: string

  /** The card form needs the provider's public key, which only comes with an approved payout setup. */
  cardAvailable: boolean
  isAuthenticated: boolean
  submitting: boolean
  submitError: PurchaseSubmitError | null
  outcome: CheckoutOutcome | null
  submit: () => Promise<void>
  /** Back to the form, keeping what was typed — after a refusal, or to change method. */
  reset: () => void
}

/**
 * The text under the coupon field. The API's own message is translated and
 * specific (expired, not for this product, limit reached), except for 429,
 * which says nothing about waiting, and for an untranslated key that slipped
 * through (BUGS.md I18N-9), which would show "monetization.coupon.x".
 */
const toCouponError = (status: number | null, message: string | null): CouponError => {
  if (status === 429) return { key: 'purchase.coupon.tooManyAttempts' }
  if (!message || /^[a-z]+(\.[a-zA-Z]+)+$/.test(message)) return { key: 'purchase.coupon.invalid' }
  return { message }
}

/**
 * One purchase, from choosing how to pay to knowing what happened.
 *
 * Product in, outcome out: the hook knows nothing about pages or routes, so the
 * pricing page, the product catalog and a future storefront space all drive
 * the same flow. Validation happens on submit, field by field, and nothing is
 * sent to the API until the buyer's data would be accepted by the provider.
 */
/** An add-on in the same order, as analytics reports it. */
export interface PurchaseFlowAddOn {
  productId: string
  title: string
  amount: number
}

/**
 * F-93 1B: buying for a company. Its admin places the order; the company is the payer and the
 * subscriber, the processor charges its CNPJ, and there is no trial.
 */
export interface CompanyPurchase {
  id: string
  name: string
  billing?: { legalName: string | null; taxId: string | null; email: string | null } | null
}

export function usePurchaseFlow(
  product: PublicProduct | null,
  addOnProductIds: string[] = [],
  addOns: PurchaseFlowAddOn[] = [],
  company: CompanyPurchase | null = null,
  /** F-116 / F-117 4: tickets of one type in one order, and the registration answers that go with it. */
  ticket: { quantity?: number; registrationAnswers?: FormAnswerInput[] } | null = null
): PurchaseFlow {
  const ticketQuantity = ticket?.quantity && ticket.quantity > 1 ? ticket.quantity : 1
  const ticketExtras = {
    ...(ticketQuantity > 1 ? { quantity: ticketQuantity } : {}),
    ...(ticket?.registrationAnswers?.length ? { registrationAnswers: ticket.registrationAnswers } : {})
  }
  const ticketKey = JSON.stringify(ticketExtras)
  const { user } = useAuth()
  const { community } = useCommunity()
  const { config } = useCheckoutConfig()
  const { tokenizeCard } = usePagarme(Boolean(config?.cardTokenization?.publicKey))

  /* Keyed by content, not identity: a refetched product brings a new array
     with the same methods, and that must not reset a purchase in progress. */
  const allowedKey = parseAllowedPaymentMethods(product?.allowedPaymentMethods).join(',')
  const allowedMethods = useMemo(
    () => (allowedKey ? (allowedKey.split(',') as PaymentMethod[]) : []),
    [allowedKey]
  )

  /* An annual option starts at its most installments: that is the price the
     pricing page shows ("12× de R$ 99"). Everything else starts at one. */
  const defaultInstallments = yearlyInstallments(product)

  const [method, setMethodState] = useState<PaymentMethod | null>(null)
  const [installments, setInstallments] = useState(1)
  const [customer, setCustomer] = useState<CustomerData>(emptyCustomerData)
  /* The CPF/CNPJ on the account, digits only ('' when none). Without one, the
     API keeps the document of the first confirmed payment on its own. */
  const [savedDocument, setSavedDocument] = useState('')
  const [saveDocument, setSaveDocument] = useState(false)
  const [ageDeclared, setAgeDeclared] = useState(false)
  const [ageDeclarationError, setAgeDeclarationError] = useState(false)
  const changeAgeDeclared = useCallback((checked: boolean) => {
    setAgeDeclared(checked)
    if (checked) setAgeDeclarationError(false)
  }, [])
  const [card, setCard] = useState<CardData>(emptyCardData)
  const [fieldErrors, setFieldErrors] = useState<PurchaseFieldErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<PurchaseSubmitError | null>(null)
  const [outcome, setOutcome] = useState<CheckoutOutcome | null>(null)
  const [shipping, setShipping] = useState<ShippingForm>(() => emptyShipping())
  const [shippingErrors, setShippingErrors] = useState<Partial<Record<ShippingField, string>>>({})
  /* The public catalog does not say a product is physical; the API does, with a 400 on the address. */
  const [askedShipping, setAskedShipping] = useState(false)
  /* FIN-7: the code the buyer asked for. It survives a change of product or
     method on purpose — switching monthly to annual should not lose it — and
     the server says whether it still applies on every new quote. */
  const [couponCode, setCouponCode] = useState<string | null>(null)
  const [couponError, setCouponError] = useState<CouponError | null>(null)

  /* A new product starts a new purchase. The buyer's data stays: it is the
     same person, and retyping a CPF to compare two plans is pure friction. */
  useEffect(() => {
    /* An annual option is sold as "12× on the card": open on the card, where
       that price lives. PIX stays the default for everything else. */
    setMethodState(
      defaultInstallments > 1 && allowedMethods.includes('CREDIT_CARD') ? 'CREDIT_CARD' : defaultPaymentMethod(allowedMethods)
    )
    setInstallments(defaultInstallments)
    setCard(emptyCardData())
    setFieldErrors({})
    setShippingErrors({})
    setSubmitError(null)
    setOutcome(null)
  }, [product?.id, allowedMethods, defaultInstallments])

  /* Prefilled from the account, never overwriting what was typed. The phone
     and the CPF/CNPJ are not in the session, so they come from `/auth/me`. */
  const companyId = company?.id ?? null
  const companyName = company?.billing?.legalName || company?.name || ''
  const companyEmail = company?.billing?.email || ''
  const companyDocument = company?.billing?.taxId || ''
  useEffect(() => {
    if (!user) return
    /* F-93 1B: the company pays, so its billing data (legal name, billing e-mail, CNPJ) fills the form. */
    if (companyId) setCustomer((prev) => ({ ...prev, name: companyName || prev.name, email: companyEmail || prev.email, document: companyDocument || prev.document }))
    else setCustomer((prev) => ({
      ...prev,
      name: prev.name || user.name || '',
      email: prev.email || user.email || ''
    }))

    let current = true
    sdk.auth
      .me()
      .then((me) => {
        if (!current) return
        const phone = nationalBrazilianPhone(typeof me?.phone === 'string' ? me.phone : null)
        if (phone) setCustomer((prev) => ({ ...prev, phone: prev.phone || phone }))
        const document = companyId ? '' : accountDocument(me)
        setSavedDocument(document)
        if (document) setCustomer((prev) => ({ ...prev, document: prev.document || document }))
      })
      .catch(() => {
        /* Prefill is a convenience; the buyer can type the number. */
      })
    return () => {
      current = false
    }
  }, [user, companyId, companyName, companyEmail, companyDocument])

  const typedDocument = onlyDigits(customer.document)
  const canSaveDocument = Boolean(!companyId && user && savedDocument && typedDocument.length >= 11 && typedDocument !== savedDocument)

  /* A monthly subscription bills per period, so it is never split; an annual
     one is one purchase a year, split on the card (F-36). The plans listing
     does not send `type`, hence the billing interval as the second signal. */
  const isSubscription = product?.type === 'SUBSCRIPTION' || Boolean(product?.billingInterval)
  const yearly = isYearly(product)
  const maxInstallments =
    method === 'CREDIT_CARD' && product
      ? yearly
        ? yearlyInstallments(product)
        : isSubscription
          ? 1
          : Math.max(1, Math.min(12, Number(product.maxInstallments) || 1))
      : 1

  /* Clamped: switching from an annual option to a monthly one renders once
     with the old count before the reset effect runs, and the API refuses
     installments above the product's maximum. */
  const chargedInstallments = Math.max(1, Math.min(installments, maxInstallments))

  /* F-74: a price of zero is a claim, not a payment (INSTALLMENT is never free: the API refuses it). */
  const isFree = Boolean(product) && Number(product?.price) === 0 && product?.type !== 'INSTALLMENT'
  const needsShipping = product?.kind === 'physical' || askedShipping
  const quote = usePriceQuote(community?.id, product, isFree ? null : method, chargedInstallments, addOnProductIds, couponCode, companyId, ticketQuantity)

  /* A refused coupon is dropped, which quotes again without it, and the
     reason stays under the field. 429 is the API throttling wrong codes; its
     text says nothing about when to try again, so ours does. */
  const couponRejection = quote.couponRejection
  useEffect(() => {
    if (!couponRejection) return
    setCouponCode((current) => (current === couponRejection.code ? null : current))
    setCouponError(toCouponError(couponRejection.status, couponRejection.message))
  }, [couponRejection])

  /* Matched by code, not by loading state: while a new method or installment
     count is quoted, the previous quote (same coupon) stays on screen. */
  const quotedCoupon = quote.quote?.coupon
  const appliedCoupon = couponCode && quotedCoupon && quotedCoupon.code.toUpperCase() === couponCode ? quotedCoupon : null
  const discountAmount = appliedCoupon ? parseFloat(quote.quote?.discountAmount ?? quote.quote?.breakdown?.discount ?? '0') || 0 : 0
  const couponPending = Boolean(couponCode) && !appliedCoupon && quote.loading

  const applyCoupon = useCallback((code: string) => {
    const normalized = code.trim().toUpperCase()
    if (!normalized) {
      setCouponError({ key: 'purchase.coupon.empty' })
      return
    }
    setCouponError(null)
    setSubmitError(null)
    setCouponCode(normalized)
  }, [])

  const removeCoupon = useCallback(() => {
    setCouponCode(null)
    setCouponError(null)
  }, [])
  const addOnKey = addOnProductIds.join(',')

  /* When the quote cannot be had, the list price stands in only if the
     community pays the fees: then the list price is exactly what is charged.
     When the buyer pays them, the total is unknown and must not be guessed;
     the same holds for installments whose interest the buyer pays. */
  // F-43: by card in installments the charge carries the interest (when the buyer pays it).
  const totalAmount = isFree ? 0 : quote.quote
    ? parseFloat(quote.quote.totalWithInterest ?? quote.quote.totalAmount)
    : quote.failed && product && product.feePayer !== 'BUYER' && addOnProductIds.length === 0 && !couponCode
        && (chargedInstallments === 1 || product.installmentInterestPayer === 'ORGANIZATION')
      ? parseFloat(product.price)
      : null
  const currency = quote.quote?.currency || product?.currency || 'BRL'
  /* A coupon that zeroes a one-off total makes the order a claim too (the API answers `free: true`): no card, no buyer data.
     A plan can be zeroed as well (person or company): the API only lets a FOREVER coupon do it, so nothing is ever charged later.
     INSTALLMENT is never free: the API refuses it. */
  const couponZeroed = !isFree && Boolean(appliedCoupon) && product?.type !== 'INSTALLMENT' && (quote.quote?.free === true || (addOnProductIds.length === 0 && totalAmount === 0))
  const skipPayment = isFree || couponZeroed

  /* The order as the community's Google Analytics sees it: the product, its
     add-ons, the total the buyer sees and the coupon the server applied. */
  const cart = useMemo<GaCart | null>(() => {
    if (!product) return null
    return {
      items: [
        gaItem(product),
        ...addOns.map((addOn) => gaItem({ id: addOn.productId, title: addOn.title }, { category: 'add_on', price: addOn.amount }))
      ],
      value: totalAmount,
      currency,
      coupon: appliedCoupon?.code ?? null
    }
  }, [product, addOns, totalAmount, currency, appliedCoupon?.code])
  const cartRef = useRef(cart)
  cartRef.current = cart

  /* `begin_checkout`: once per product shown in this checkout. */
  const checkoutStartedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!product?.id || checkoutStartedRef.current === product.id || !cartRef.current) return
    const productId = product.id
    const started = { ...cartRef.current, value: null, coupon: null }
    /* After the page's own `view_item`: the page's effects run after this
       panel's, and GA reads the funnel in the order events arrive. */
    const timer = window.setTimeout(() => {
      checkoutStartedRef.current = productId
      trackBeginCheckout(started)
    }, 0)
    return () => window.clearTimeout(timer)
  }, [product?.id])

  const cardAvailable = Boolean(config?.cardTokenization?.publicKey)

  const setMethod = useCallback((next: PaymentMethod) => {
    setMethodState(next)
    setInstallments(defaultInstallments)
    setSubmitError(null)
    setFieldErrors((prev) => ({ ...prev, number: undefined, holderName: undefined, expiry: undefined, cvv: undefined }))
  }, [defaultInstallments])

  const setCustomerField = useCallback((field: CustomerField, value: string) => {
    setCustomer((prev) => ({ ...prev, [field]: value }))
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev))
  }, [])

  const setCardField = useCallback((field: CardField, value: string) => {
    setCard((prev) => ({ ...prev, [field]: value }))
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev))
  }, [])

  const setShippingField = useCallback((field: ShippingField, value: string) => {
    setShipping((prev) => ({ ...prev, [field]: value }))
    setShippingErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev))
  }, [])

  const submit = useCallback(async () => {
    if (!product || (!skipPayment && !method) || submitting) return
    setSubmitError(null)

    if (!user) {
      setSubmitError({ key: 'purchase.errors.signInRequired' })
      return
    }

    const errors: PurchaseFieldErrors = skipPayment ? {} : { ...validateCustomerData(customer) }
    // F-93 1B: the company pays with its CNPJ; a CPF is refused by the API (`monetization.company.cnpjRequired`).
    if (!skipPayment && companyId && !errors.document && onlyDigits(customer.document).length !== 14) errors.document = 'purchase.errors.cnpjRequired'
    if (!skipPayment && method === 'CREDIT_CARD') Object.assign(errors, validateCardData(card))
    setFieldErrors(errors)
    const addressErrors = needsShipping ? validateShipping(shipping) : {}
    setShippingErrors(addressErrors)
    const ageMissing = !skipPayment && !ageDeclared
    setAgeDeclarationError(ageMissing)
    if (Object.values(errors).some(Boolean) || Object.values(addressErrors).some(Boolean) || ageMissing) return

    if (totalAmount === null) {
      setSubmitError({ key: 'purchase.errors.priceUnavailable' })
      return
    }
    /* A code still being checked would be charged at a price the buyer has
       not seen; the button is disabled while the quote loads, this is the
       guard behind it. */
    if (couponCode && !appliedCoupon) return

    setSubmitting(true)
    try {
      if (!skipPayment && cartRef.current) trackAddPaymentInfo(cartRef.current, method)
      /* GA's client and session ids, so the server's own `purchase` joins this browser's (and is not counted twice). */
      const gaIds = await getGaIds()

      let cardToken: string | undefined
      if (!skipPayment && method === 'CREDIT_CARD') {
        if (!config?.cardTokenization?.publicKey) {
          setSubmitError({ key: 'purchase.errors.cardUnavailable' })
          return
        }
        try {
          cardToken = await tokenizeCard(toTokenizeInput(card), config.cardTokenization.publicKey)
        } catch {
          setSubmitError({ key: 'purchase.errors.cardRejected' })
          return
        }
      }

      const response = await sdk.checkout.create(isFree ? {
        // F-74: no method, no buyer data; the price is the server's.
        productId: product.id,
        ...ticketExtras,
        ...(companyId ? { companyId } : {}),
        ...(needsShipping ? { shippingAddress: toShippingPayload(shipping) } : {}),
        ...gaIds
      } : couponZeroed ? {
        // The coupon takes the total to zero: nothing is charged, so no card and no buyer data; the server confirms with `free: true`.
        productId: product.id,
        ...ticketExtras,
        ...(companyId ? { companyId } : {}),
        ...(needsShipping ? { shippingAddress: toShippingPayload(shipping) } : {}),
        // F74-A2: optional on a zeroed order; sent only when one is picked.
        ...(method ? { paymentMethod: method } : {}),
        installments: 1,
        couponCode: appliedCoupon!.code,
        ...gaIds
      } : {
        productId: product.id,
        ...ticketExtras,
        ...(companyId ? { companyId } : {}),
        ...(needsShipping ? { shippingAddress: toShippingPayload(shipping) } : {}),
        paymentMethod: method,
        installments: chargedInstallments,
        // F-204: the buyer's age declaration, only on an order that costs money.
        ageDeclaration: true,
        // F-37: one order, one charge for the plan and its add-ons.
        ...(addOnKey ? { addOnProductIds: addOnKey.split(',') } : {}),
        // FIN-7: the same code the quote applied; the server checks it again.
        ...(appliedCoupon ? { couponCode: appliedCoupon.code } : {}),
        ...(cardToken ? { cardToken } : {}),
        customerData: toCustomerPayload(customer),
        // The account keeps this CPF/CNPJ instead of the saved one.
        ...(canSaveDocument && saveDocument ? { saveDocument: true } : {}),
        ...gaIds
      })

      setOutcome(interpretCheckoutResponse(response, method))
    } catch (error) {
      const { message, status, param, data } = apiErrorMessage(error)
      /* F-74: the address was refused (a physical product the catalog did not
         flag, or a field the API checks by country): open the form and mark the field. */
      /* F-204: the API did not take the age declaration (nothing was held or created): ask for the box again. */
      if (status === 400 && param === 'ageDeclaration') {
        setAgeDeclared(false)
        setAgeDeclarationError(true)
        return
      }
      const shipField = shippingFieldOf(param)
      if (status === 400 && shipField) {
        setAskedShipping(true)
        if (shipField === 'form') setShippingErrors({})
        else setShippingErrors((prev) => ({ ...prev, [shipField]: message || 'shipping.errors.invalid' }))
        setSubmitError(shipField === 'form' ? { key: 'products:shipping.needed' } : null)
        return
      }
      /* The same plan is held already, or has a PIX/boleto still open (409 on
         productId; nothing was created). An open payment is the order the
         buyer already has: follow it instead of making another. */
      if (status === 409 && param === 'productId') {
        const conflict = (data || {}) as { openPayment?: CheckoutResponse & { purchaseId?: string }; subscriptionId?: string }
        if (conflict.openPayment?.purchaseId) {
          const open = conflict.openPayment
          setOutcome({ ...interpretCheckoutResponse(open, (open.paymentMethod as PaymentMethod) || method), productId: open.productId ?? null })
          return
        }
        if (conflict.subscriptionId) {
          setSubmitError({ message: message || undefined, key: 'purchase.alreadySubscribed', alreadySubscribed: true })
          return
        }
        /* Any other 409 on productId is the last unit going to someone else
           ("Esgotado", F-29): nothing was charged, and it is not "you already
           have this". Say it as the API did and go no further. */
        const soldOut = Boolean((data as { soldOut?: boolean } | null)?.soldOut) || !conflict.openPayment
        setSubmitError({ ...(message ? { message } : { key: 'purchase.soldOut' }), soldOut })
        return
      }
      /* The document to keep failed the API's check digit: nothing was
         created, and the reason belongs under the field. */
      if (status === 400 && param === 'customerData.document') {
        setFieldErrors((prev) => ({ ...prev, document: message || 'purchase.errors.cpfInvalid' }))
        return
      }
      /* FIN-7: the coupon stopped applying between the quote and the charge
         (its last use was taken, it expired). Nothing was charged; drop it so
         the summary shows the full price before the buyer tries again. */
      if (appliedCoupon && (param === 'couponCode' || status === 429)) {
        setCouponCode(null)
        setCouponError(toCouponError(status, message))
        setSubmitError({ key: 'purchase.coupon.removedBeforeCharge' })
        return
      }
      /* An add-on bought without one of its plans (F-33). The API also answers
         403 on productId for a product that is not for sale, so the product's
         own prerequisites tell the two apart. */
      if (status === 403 && param === 'productId' && product.prerequisiteGroupIds?.length) {
        setSubmitError({ key: 'purchase.errors.prerequisiteRequired', needsPlan: true })
        return
      }
      /* 4xx bodies are specific and translated by the API (product not for
         sale here, already purchased, out of stock). A 5xx says nothing the
         buyer can act on. */
      if (status && status >= 400 && status < 500 && message) setSubmitError({ message })
      else setSubmitError({ key: 'purchase.errors.generic' })
    } finally {
      setSubmitting(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `ticketExtras` is read through `ticketKey` (same content, same callback)
  }, [product, method, submitting, isFree, couponZeroed, skipPayment, needsShipping, shipping, user, customer, card, totalAmount, config, tokenizeCard, chargedInstallments, addOnKey, couponCode, appliedCoupon, canSaveDocument, saveDocument, companyId, ticketKey, ageDeclared])

  /* Following a pending purchase: the screen flips to "paid" (or "refused") by itself. */
  usePendingPurchasePoll(outcome, setOutcome)

  /* An order exists: remember it for whichever screen sees it paid, and
     report `purchase` as soon as it is (a card at once, a free claim, a PIX
     or boleto when the poll sees it). A resumed order for another option is
     left to that option's own record. */
  useEffect(() => {
    const purchaseId = outcome?.purchaseId
    const current = cartRef.current
    if (!outcome || !purchaseId || outcome.kind === 'refused' || !current) return
    if (outcome.productId && outcome.productId !== product?.id) return
    const paidWith = outcome.kind === 'free' ? 'FREE' : method
    if (outcome.kind === 'paid' || outcome.kind === 'free') trackPurchase(purchaseId, current, paidWith)
    else rememberOrder(purchaseId, current, paidWith)
    // Once per outcome; the cart is read from the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome])

  const reset = useCallback(() => {
    setOutcome(null)
    setSubmitError(null)
  }, [])

  return {
    product,
    allowedMethods,
    method,
    setMethod,
    installments: chargedInstallments,
    setInstallments,
    maxInstallments,
    showInstallmentAmounts: yearly,
    savesCard: isSubscription && method === 'CREDIT_CARD',
    customer,
    setCustomerField,
    card,
    setCardField,
    fieldErrors,
    canSaveDocument,
    saveDocument,
    setSaveDocument,
    isFree: skipPayment,
    ageDeclaration: { checked: ageDeclared, onChange: changeAgeDeclared, error: ageDeclarationError },
    couponZeroed,
    needsShipping,
    shipping,
    setShippingField,
    shippingErrors,
    quote,
    appliedCoupon,
    discountAmount,
    couponPending,
    couponError,
    applyCoupon,
    removeCoupon,
    totalAmount,
    currency,
    cardAvailable,
    isAuthenticated: Boolean(user),
    submitting,
    submitError,
    outcome,
    submit,
    reset
  }
}
